import { motion, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import GlobalHeader from "@/components/GlobalHeader";
import PricingStageTimeline from "@/components/PricingStageTimeline";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { PHOTOS } from "@/lib/photos";
import { REMAINING_PCT } from "@/lib/inventory";
import { usePageSEO } from "@/hooks/use-page-seo";
import { buildMusicEventSchema } from "@/lib/event-schema";
import Footer from "@/components/Footer";
import SponsorsBlock from "@/components/SponsorsBlock";
import TicketsUrgencyBar from "@/components/TicketsUrgencyBar";
import HeroDoorsCountdown from "@/components/HeroDoorsCountdown";
import { Check, Star, Anchor, Music, Utensils, GlassWater, PartyPopper, Crown } from "lucide-react";
import CaptainsWelcomeBand from "@/components/CaptainsWelcomeBand";

// ── Queue Config ────────────────────────────────────────────
const QUEUE_ENABLED = false;
const MIN_QUEUE_POSITION = 180;
const MAX_QUEUE_POSITION = 320;
const TICK_INTERVAL_MIN = 18000;
const TICK_INTERVAL_MAX = 28000;
const TICK_DROP_MIN = 50;
const TICK_DROP_MAX = 90;
// ────────────────────────────────────────────────────────────

// (Wave 2 countdown component removed 2026-07-03 — it was dead code targeting a past date)

function rand(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function estimatedWait(pos: number) {
  if (pos > 200) return "under 3 minutes";
  if (pos > 100) return "under 2 minutes";
  return "under 1 minute";
}

const PulsingDots = () => (
  <div className="flex items-center justify-center gap-1.5 my-6">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="block w-2 h-2 rounded-full bg-[hsl(var(--yacht-charcoal)/0.3)]"
        style={{
          animation: "pulse-dot 1.4s ease-in-out infinite",
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes pulse-dot {
        0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
        40% { opacity: 1; transform: scale(1); }
      }
    `}</style>
  </div>
);

const gaFeatures = [
  { icon: Music, text: "Six national headliners Saturday — Three Dog Night, Ambrosia, Elliot Lurie, Peter Beckett, Walter Egan & John Ford Coley" },
  { icon: PartyPopper, text: "The High Noon Deck Party — a floating DJ deck, literally on the Detroit River" },
  { icon: Anchor, text: "Run of the Roostertail: Marine Room, riverfront patio, every outdoor space" },
  { icon: Utensils, text: "Themed food & drink available for purchase across both floors (cocktails, beer, wine, snacks)" },
];

const vipFeatures = [
  { icon: Crown, text: "Roostertail Lounge — your private home base with comfort & seating" },
  { icon: Star, text: "Priority entry — skip the line at the gate" },
  { icon: Music, text: "Six national headliners Saturday — Three Dog Night, Ambrosia, Elliot Lurie, Peter Beckett, Walter Egan & John Ford Coley" },
  { icon: PartyPopper, text: "The High Noon Deck Party — a floating DJ deck, literally on the Detroit River" },
  { icon: Anchor, text: "Run of all open areas — including the Marine Room and Palm River Room" },
];

const TicketAccordionRow = ({
  tier,
  subLabel,
  features,
  isVip = false,
  isOpen,
  onToggle,
  onBuy,
  isDisabled = false,
  customBody,
  badgeText,
}: {
  tier: string;
  subLabel: string;
  features?: { icon: React.ElementType; text: string }[];
  isVip?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onBuy: () => void;
  isDisabled?: boolean;
  customBody?: React.ReactNode;
  badgeText?: string;
}) => {
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-yacht-charcoal border border-yacht-charcoal/15"
      style={{ boxShadow: "0 12px 32px -16px rgba(0,0,0,0.35), 0 4px 12px -6px rgba(0,0,0,0.18)" }}
    >
      <span
        className={`absolute left-0 top-0 bottom-0 w-1 md:w-1.5 ${
          isDisabled ? "bg-white/15" : "bg-yacht-yellow"
        }`}
        aria-hidden
      />

      <button
        onClick={isDisabled ? undefined : onToggle}
        disabled={isDisabled}
        className={`w-full flex items-center justify-between gap-3 py-6 md:py-8 pl-6 md:pl-10 pr-5 md:pr-8 transition-colors text-left ${
          isDisabled
            ? "opacity-60 cursor-not-allowed"
            : "hover:bg-white/5 cursor-pointer"
        }`}
        aria-expanded={isOpen}
      >
        <span className="flex flex-col gap-1 md:flex-row md:items-baseline md:gap-5 min-w-0">
          <span
            className={`text-xl sm:text-2xl md:text-4xl font-bold tracking-tight uppercase leading-tight ${
              isDisabled ? "text-white/60" : "text-white"
            }`}
          >
            {tier}
          </span>
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] sm:text-[11px] md:text-sm font-semibold tracking-wider whitespace-nowrap text-white/55">
              {subLabel}
            </span>
            {badgeText && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yacht-yellow text-yacht-charcoal text-[9px] md:text-[10px] font-bold uppercase tracking-[0.15em] whitespace-nowrap">
                {badgeText}
              </span>
            )}
          </span>
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-3xl md:text-4xl font-light leading-none w-6 md:w-7 text-center flex-shrink-0"
          style={{ color: isDisabled ? "rgba(255,255,255,0.4)" : "#F7B41C" }}
        >
          {isDisabled ? "—" : "+"}
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && !isDisabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="px-6 md:px-10 pb-8 md:pb-10">
              {customBody ? (
                <div className="max-w-3xl">
                  {customBody}
                </div>
              ) : (
              <div className="max-w-2xl">
              {isVip && (
                <div className="mb-6 rounded-xl bg-yacht-yellow/10 border border-yacht-yellow/20 p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <GlassWater className="w-5 h-5 text-yacht-yellow" />
                    <span className="text-yacht-yellow font-bold text-sm uppercase tracking-wide">
                      Drinks + Food in the VIP Lounge
                    </span>
                  </div>
                  <p className="text-white/80 text-xs md:text-sm leading-relaxed">
                    Complimentary drinks and food are served only inside the Roostertail VIP Lounge — your VIP-only home base — all day. Anywhere else in the venue, food and drinks are available for purchase.
                  </p>
                  <a
                    href="/admirals-menu"
                    className="inline-flex items-center gap-1.5 mt-3.5 px-4 py-2 rounded-full border border-yacht-yellow/50 text-yacht-yellow text-xs md:text-sm font-bold uppercase tracking-wide hover:bg-yacht-yellow hover:text-yacht-charcoal transition-colors"
                  >
                    View Food Menu →
                  </a>
                  <p className="text-white/40 text-[11px] leading-relaxed mt-2">
                    Menu subject to change.
                  </p>
                </div>
              )}

              <ul className="space-y-3 md:space-y-4 mb-7">
                {(features ?? []).map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-yacht-yellow/20 flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-yacht-yellow" strokeWidth={3} />
                    </span>
                    <span className="text-sm md:text-base text-white/85 leading-relaxed">
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={onBuy}
                className="w-full md:w-auto md:px-14 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base md:text-lg hover:brightness-110 transition-all cursor-pointer"
                style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
              >
                Get Tickets →
              </button>
              </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const saturdayPreview = [
  "Three Dog Night",
  "Ambrosia + Peter Beckett, Walter Egan, Elliot Lurie, John Ford Coley",
  "Party Pupils (DJ set)",
];

// Day One is over — Saturday is the only day left to sell. One card, full width.
const SaturdayBody = ({ onBuy }: { onBuy: () => void }) => (
  <div className="space-y-5">
    <p className="text-white/65 text-xs md:text-sm leading-relaxed">
      Day One was insane — and we're back, bigger and better. Full venue access, every act,
      the High Noon Deck Party — all included. GA and VIP options shown at checkout.
    </p>

    <div className="relative rounded-xl p-5 flex flex-col gap-4 border bg-yacht-yellow/[0.07] border-yacht-yellow/30">
      <span className="absolute -top-2 left-4 inline-flex items-center px-2 py-0.5 rounded-full bg-yacht-yellow text-yacht-charcoal text-[9px] font-bold uppercase tracking-[0.15em] whitespace-nowrap">
        Headliner: Three Dog Night
      </span>

      <div>
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <h4 className="text-white font-bold text-lg md:text-xl uppercase tracking-tight">
            Saturday
          </h4>
        </div>
        <p className="text-white/55 text-[11px] uppercase tracking-wider font-semibold">
          July 18 · Noon – Midnight
        </p>
        <p className="text-[12px] mt-1.5 font-semibold text-yacht-yellow">
          6 national acts + Party Pupils + DJs + local bands
        </p>
      </div>

      <ul className="space-y-1.5">
        {saturdayPreview.map((name) => (
          <li
            key={name}
            className="flex items-center gap-2 text-white/80 text-[13px] leading-tight"
          >
            <Music className="w-3 h-3 text-yacht-yellow flex-shrink-0" />
            {name}
          </li>
        ))}
      </ul>

      <button
        onClick={onBuy}
        className="mt-auto w-full rounded-full py-3 px-5 font-bold text-sm uppercase tracking-wide transition-all flex items-center justify-center gap-1.5 bg-yacht-yellow text-yacht-charcoal hover:brightness-110"
        style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
      >
        Get Saturday Tickets →
      </button>
    </div>

    <p className="text-white/45 text-[11px] md:text-xs text-center leading-relaxed">
      This is it — one more day. Don't miss it, the next one is 365 days away.
    </p>
  </div>
);

const Tickets = () => {
  const ticketsJsonLd = useMemo(() => buildMusicEventSchema({
    url: "https://yachtrockdetroit.com/tickets",
    description: "Saturday tickets for Yacht Rock Detroit 2026 at The Roostertail — the final day, July 18. Three Dog Night, Ambrosia, Elliot Lurie, Peter Beckett, Walter Egan & John Ford Coley live on the Detroit River. Doors at noon.",
  }), []);

  usePageSEO({
    title: "Saturday Tickets — Yacht Rock Detroit 2026 | One More Day",
    description: "One more day! Saturday tickets for Yacht Rock Detroit 2026 at The Roostertail, July 18. Three Dog Night, Ambrosia & more — doors at noon, music until midnight.",
    keywords: "yacht rock detroit tickets, concert tickets detroit, detroit festival tickets 2026, summer concert tickets detroit, roostertail detroit tickets, detroit waterfront concert tickets, yacht rock concert tickets",
    ogImage: PHOTOS.CROWD.sunsetStageView,
    jsonLd: ticketsJsonLd,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Tickets", url: "https://yachtrockdetroit.com/tickets" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);

  // ── Accordion state — all tiers open by default (mobile buyers must see every
  // option + buy button without tapping; independent toggles = no layout jumps) ──
  const [openTiers, setOpenTiers] = useState<Set<"ga" | "vip" | "single">>(
    () => new Set(["single", "ga", "vip"] as const)
  );
  const toggleTier = (tier: "ga" | "vip" | "single") =>
    setOpenTiers((prev) => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier);
      else next.add(tier);
      return next;
    });

  // ── Value modal state ──

  // ── Queue state ──
  const [phase, setPhase] = useState<"idle" | "loading" | "queue" | "cleared">("idle");
  const [position, setPosition] = useState(0);
  const [startPosition, setStartPosition] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const skipQueue = new URLSearchParams(window.location.search).get("skip") === "true";

  const suppressModalZIndex = useCallback(() => {
    const observer = new MutationObserver(() => {
      document.querySelectorAll('body > div, body > iframe').forEach(el => {
        const style = window.getComputedStyle(el);
        const z = parseInt(style.zIndex);
        if (z >= 99999 && !el.closest('.queue-overlay')) {
          (el as HTMLElement).style.setProperty('z-index', '99998', 'important');
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  }, []);

  // Tickets is the #1 page we WANT indexed — explicitly allow it (overrides any
  // stale robots meta) so search engines rank it for "Detroit concert tickets" etc.
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "index, follow, max-image-preview:large";
  }, []);

  // Load OnTourHQ modal script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://events.ontourhq.com/ts_modal.js";
    script.async = true;
    script.onload = () => {
      (window as any).TSModals?.buildModal({
        url: "https://events.ontourhq.com/e/yrd/tickets",
        modalTriggerElementId: "modal-trigger-element-id",
      });
    };
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const scheduleTick = useCallback(() => {
    const interval = rand(TICK_INTERVAL_MIN, TICK_INTERVAL_MAX);
    timerRef.current = setTimeout(() => {
      setPosition((prev) => {
        const drop = rand(TICK_DROP_MIN, TICK_DROP_MAX);
        const next = Math.max(0, prev - drop);
        if (next > 0) scheduleTick();
        return next;
      });
    }, interval);
  }, []);

  // When queue reaches 0, transition to cleared
  useEffect(() => {
    if (phase === "queue" && position === 0 && startPosition > 0) {
      setPhase("cleared");
    }
  }, [phase, position, startPosition]);

  // Cleared → idle after 2s
  useEffect(() => {
    if (phase !== "cleared") return;
    const t = setTimeout(() => {
      setPhase("idle");
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
        document.querySelectorAll('body > div, body > iframe').forEach(el => {
          if ((el as HTMLElement).style.getPropertyValue('z-index') === '99998') {
            (el as HTMLElement).style.removeProperty('z-index');
          }
        });
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [phase]);

  // Cleanup timer on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const releaseSuppression = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
      document.querySelectorAll('body > div, body > iframe').forEach(el => {
        if ((el as HTMLElement).style.getPropertyValue('z-index') === '99998') {
          (el as HTMLElement).style.removeProperty('z-index');
        }
      });
    }
  }, []);

  const handleGetTickets = () => {
    // Fire ViewContent when the buyer opens the ticket selector — the OnTourHQ
    // iframe is cross-domain, so this click is the deepest funnel event the
    // pixel can see client-side.
    (window as any).fbq?.("track", "ViewContent", {
      content_name: "Yacht Rock Detroit 2026 Tickets",
      content_category: "Tickets",
    });
    // Open the OnTourHQ checkout directly.
    document.getElementById("modal-trigger-element-id")?.click();
  };

  const progress = startPosition > 0 ? ((startPosition - position) / startPosition) * 100 : 0;
  const overlayVisible = phase !== "idle";

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream pb-20 md:pb-0">
      <GlobalHeader heroRef={headerRef} />
      <TicketsUrgencyBar />

      {/* Sticky mobile buy bar — the CTA lives in the thumb zone at all times.
          Sits below the OnTourHQ checkout overlay (z ~2.1B), so it disappears
          automatically while checkout is open. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-yacht-cream/95 backdrop-blur-sm border-t border-yacht-charcoal/10">
        <button
          onClick={handleGetTickets}
          className="w-full py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base hover:brightness-110 transition-all"
          style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
        >
          Get Saturday Tickets · One More Day →
        </button>
      </div>

      {/* Hidden modal trigger */}
      <button
        id="modal-trigger-element-id"
        aria-hidden="true"
        style={{ position: "fixed", left: "-9999px", width: "1px", height: "1px", opacity: 0.01 }}
      />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative min-h-fit md:min-h-[70vh] bg-yacht-blue overflow-hidden flex items-center justify-center"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{ opacity: 0.15, filter: "brightness(0) invert(1)" }}
        />

        {/* TEXT-ONLY HERO — static (no entrance animation: buyers from ads must
            see the message + CTA instantly, even mid-scroll) */}
        <div className="relative z-20 w-full flex flex-col items-center justify-center text-center pt-28 pb-14 md:pt-48 md:pb-32 px-6">
          <p className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-5">
            Yacht Rock Detroit · Saturday, July 18
          </p>
          <h1 className="text-[13vw] md:text-[7.5vw] font-bold text-white leading-[0.95] tracking-tight">
            This Is It.
            <br />
            One More Day
          </h1>
          <p
            className="mt-5 text-white/85 text-base md:text-xl italic max-w-xl mx-auto"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            Day One was insane. We're back — bigger and better — with the biggest bill of the weekend.
          </p>
          <HeroDoorsCountdown />
          <button
            onClick={handleGetTickets}
            className="mt-8 w-full max-w-xs md:w-auto md:px-16 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-lg hover:brightness-110 transition-all cursor-pointer"
            style={{ boxShadow: "0 6px 20px hsla(54, 93%, 49%, 0.45)" }}
          >
            Get Saturday Tickets →
          </button>
        </div>
      </section>

      <CaptainsWelcomeBand />

      {/* Venue readiness — indoor space + stages set. Editorial index, no cards.
          Static like the hero: reassurance copy must read instantly. */}
      <section className="bg-yacht-cream py-16 md:py-24 px-6 md:px-16 border-b border-yacht-charcoal/10">
        <div className="max-w-5xl mx-auto">
          <p className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-5 md:mb-6">
            Ready for Day Two
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-yacht-charcoal tracking-tight leading-[1.02] mb-4">
            Every stage is set —<br className="hidden md:block" /> indoors and outdoors.
          </h2>
          <p
            className="text-yacht-charcoal/75 text-lg md:text-2xl italic leading-snug mb-10 md:mb-14"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            Over 25,000 square feet of air-conditioned indoor space, right on the Detroit River.
          </p>
          <div className="border-t border-yacht-charcoal/20">
            {[
              {
                num: "01",
                title: "25,000+ Sq Ft Indoors",
                blurb:
                  "The Roostertail's air-conditioned interior runs the full length of the festival — cool off anytime, all day",
              },
              {
                num: "02",
                title: "Music Indoors & Out",
                blurb:
                  "Five stages: Main, Deck, and Garden on the water · Marine Room and Admirals Lounge inside",
              },
              {
                num: "03",
                title: "Day One Is in the Books",
                blurb:
                  "It was insane — and we're back, bigger and better. Doors open Saturday at noon",
              },
            ].map((item) => (
              <div
                key={item.num}
                className="flex items-baseline gap-5 md:gap-8 py-6 md:py-8 border-b border-yacht-charcoal/20"
              >
                <span className="text-yacht-charcoal/35 text-sm md:text-base font-medium tabular-nums">
                  {item.num}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-2xl md:text-4xl font-bold text-yacht-charcoal tracking-tight leading-none">
                    {item.title}
                  </span>
                  <span className="block mt-2 text-yacht-charcoal/60 text-sm md:text-base leading-relaxed">
                    {item.blurb}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stage Timeline — clean "where we are" indicator */}
      <PricingStageTimeline />

      {/* TICKET ACCORDION */}
      <section className="bg-yacht-cream py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-10 md:mb-16">
            <p className="text-yacht-blue text-sm font-semibold tracking-[0.2em] uppercase mb-3">
              Saturday, July 18, 2026 · The Roostertail · Detroit, MI
            </p>
            <h2 className="text-h1 md:text-hero text-yacht-charcoal font-bold mb-4">
              Choose Your Experience
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-6">
              One more day. Five stages. Don't miss it — the next one is 365 days away.
            </p>

            {/* Trust banner */}
            <div className="inline-flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-5 py-3 rounded-full bg-yacht-blue/5 border border-yacht-blue/15 max-w-2xl">
              <span className="inline-flex items-center gap-1.5 text-yacht-charcoal text-xs md:text-sm font-semibold">
                <Check className="w-4 h-4 text-yacht-blue" /> Saturday · Doors at Noon
              </span>
              <span className="inline-flex items-center gap-1.5 text-yacht-charcoal text-xs md:text-sm font-semibold">
                <Check className="w-4 h-4 text-yacht-blue" /> Three Dog Night Headlines
              </span>
              <span className="inline-flex items-center gap-1.5 text-yacht-charcoal text-xs md:text-sm font-semibold">
                <Check className="w-4 h-4 text-yacht-blue" /> Mobile · just show the QR
              </span>
            </div>

            {/* Songs-you-know strip — sell the catalog, not the band names */}
            <p className="mt-6 text-yacht-charcoal/70 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              You already know every word —{" "}
              <span className="font-semibold text-yacht-charcoal">
                "Joy to the World" · "Brandy (You're a Fine Girl)" · "Baby Come Back" ·
                "Love Will Find a Way" · "Biggest Part of Me" · "Magnet and Steel" ·
                "I'd Really Love to See You Tonight"
              </span>{" "}
              — live on the Detroit River.
            </p>
          </div>

          {/* Accordion List — three separate dark cards with spacing */}
          <div className="space-y-4 md:space-y-5">
            <TicketAccordionRow
              tier="Saturday Pass"
              subLabel="July 18 · Noon – Midnight"
              badgeText="This Is It — One More Day"
              customBody={<SaturdayBody onBuy={() => handleGetTickets()} />}
              isOpen={openTiers.has("single")}
              onToggle={() => toggleTier("single")}
              onBuy={() => handleGetTickets()}
            />
            <TicketAccordionRow
              tier="General Admission"
              subLabel="Saturday · Full Venue Access"
              badgeText={`Less Than ${REMAINING_PCT}% Remaining`}
              features={gaFeatures}
              isOpen={openTiers.has("ga")}
              onToggle={() => toggleTier("ga")}
              onBuy={() => handleGetTickets()}
            />
            <TicketAccordionRow
              tier="VIP Admiral's Pass"
              subLabel="Saturday · Open Bar + Food All Day"
              badgeText="Final Allocation — Once VIP Tickets Sell Out, It's Gone"
              features={vipFeatures}
              isVip
              isOpen={openTiers.has("vip")}
              onToggle={() => toggleTier("vip")}
              onBuy={() => handleGetTickets()}
            />
          </div>

          {/* Bottom note — social proof, simple */}
          <div className="mt-12 md:mt-16 text-center max-w-2xl mx-auto">
            <p
              className="text-yacht-charcoal/75 text-base md:text-lg italic leading-relaxed mb-4"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              This isn't a fairground. The Roostertail is a private waterfront club on the Detroit
              River — when it reaches capacity, sales stop.
            </p>
            <p className="text-base md:text-xl font-bold tracking-tight text-yacht-charcoal mb-3">
              <span className="text-red-600">Day One was insane.</span> Saturday is the biggest bill of the weekend.
            </p>
            <p className="text-yacht-charcoal/80 text-sm md:text-base font-semibold leading-relaxed">
              Three Dog Night · Ambrosia featuring Peter Beckett, Walter Egan, Elliot Lurie &amp; John Ford Coley · Party Pupils
            </p>
            <p className="text-yacht-blue text-[11px] md:text-sm font-bold uppercase tracking-[0.15em] mt-3">
              Plus DJs and local bands from noon to midnight
            </p>
          </div>

          <div className="max-w-3xl mx-auto mt-16 md:mt-20">
            <SponsorsBlock />
          </div>
        </div>
      </section>
      <Footer />

      {/* Queue Overlay */}
      <AnimatePresence>
        {overlayVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="queue-overlay fixed inset-0 z-[99999] flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(120,120,120,0.95)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
          >
            {phase === "queue" && (
              <motion.div
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="fixed top-0 left-0 right-0 z-[100000] text-center py-3 px-4 text-white text-[13px] leading-snug border-b border-white/10"
                style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
              >
                You're in line — we'll hold this price for you. Make sure you have your access code ready. Check your email for it.
              </motion.div>
            )}

            {phase === "queue" && (
              <div className="fixed top-[42px] left-0 right-0 h-0.5 bg-white/10">
                <motion.div
                  className="h-full bg-white/40"
                  initial={{ width: "0%" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </div>
            )}

            <motion.div
              initial={{ scale: 0.97, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.97, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 md:p-12 text-center"
            >
              {phase === "loading" && (
                <div className="py-6">
                  <PulsingDots />
                  <h2 className="text-lg font-semibold text-yacht-charcoal">
                    Loading tickets…
                  </h2>
                </div>
              )}

              {phase === "cleared" && (
                <div className="py-6">
                  <p className="text-2xl mb-3">🎉</p>
                  <h2 className="text-lg font-semibold text-yacht-charcoal">
                    You're up! Loading your tickets…
                  </h2>
                </div>
              )}

              {phase === "queue" && (
                <div className="text-center">
                  <h2 className="text-lg font-semibold text-yacht-charcoal mb-1">
                    We're holding your spot and your early bird price
                  </h2>

                  <PulsingDots />

                  <p className="text-yacht-charcoal/80 text-[15px] leading-relaxed mb-1.5">
                    There are approximately <span className="font-semibold">{position}</span> people ahead of you.
                  </p>
                  <p className="text-yacht-charcoal/55 text-sm mb-6">
                    Estimated wait: {estimatedWait(position)}
                  </p>

                  <div className="border-t border-yacht-charcoal/10 pt-5 space-y-3 text-[13px] text-yacht-charcoal/50 leading-relaxed">
                    <p>⚠️ Do not refresh this tab. You will lose your place in line.</p>
                    <p>Your early pricing is locked. Once you reach checkout, you'll have 10 minutes to complete your order.</p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Tickets;
